import { FF_ENABLE_INC_VIDEO_CALL, FF_IMMEDIATE_NURSE_CONSULTATION } from '@/constants/featureFlags';
import { GoogleAnalyticsEventName } from '@/constants/types';
import { useConsultEligibleCalendar } from '@/context/calendar-context';
import type { PreScreeningData } from '@/context/pre-screening-context';
import { usePrescreeningData } from '@/context/pre-screening-context';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import {
  ImmediateNurseConsultationVariant,
  useImmediateNurseConsultVariant,
} from '@/hooks/useImmediateNurseConsultVariant';
import { AddDiscountCode } from '@/pages/components/discount-code/AddDiscountCode';
import { CtaButton, CtaSection } from '@/pages/components/styled/Cta';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getSearchParams } from '@/utils/url';
import { useFormBuilder } from '@montugroup/form-builder';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect } from 'react';

export const EligibilityVariantImmediateConsult = () => {
  const { flags } = useFeatureFlags();
  const isImmediateNurseConsultationEnabled = flags[FF_IMMEDIATE_NURSE_CONSULTATION];
  const ffEnableINCVideoCall = flags[FF_ENABLE_INC_VIDEO_CALL];
  const immediateConsultVariant = useImmediateNurseConsultVariant();
  const { handleShowCalendar } = useConsultEligibleCalendar();
  const { setPrescreeningData } = usePrescreeningData();

  const { setCurrentStepIndex } = useFormBuilder();

  useEffect(() => {
    setPrescreeningData((current: PreScreeningData) => {
      return {
        ...current,
        immediateNurseConsultation: {
          variant: immediateConsultVariant,
        },
      };
    });
  }, [immediateConsultVariant, setPrescreeningData]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  const userEmail = getSearchParams('email', { localStorageKey: 'prescreeningUserEmail' });
  const promocode = getSearchParams('promocode');
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';

  type BookingType = 'immediate_nurse_consultation' | 'scheduled_consultation';

  const handleAnalyticsOnClick = (bookingType: BookingType) => {
    const trackingPayload = {
      event_category: 'cal.com',
      event_label:
        bookingType === 'immediate_nurse_consultation' ? 'Immediate Nurse Consultation' : 'Initial Consultation',
      sha_email: shaEmail,
      ...(promocode ? { promocode } : {}),
    };
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, trackingPayload);
  };

  const handleOnNextClick = () => {
    setCurrentStepIndex((prev) => prev + 1);
  };

  const showScheduleForLaterButton: boolean = [
    ImmediateNurseConsultationVariant.VARIANT,
    ImmediateNurseConsultationVariant.VARIANT_AMAZON_CONNECT_INITIATED_INC,
  ].includes(immediateConsultVariant);

  const speakToANurseNowText = 'Speak to a nurse now';
  const averageWaitText = '(Average wait 5 - 10mins)';

  return (
    <Box data-testid="eligibility-variant">
      {ffEnableINCVideoCall ? (
        <Typography
          variant="h6"
          component="h2"
          color={'primary'}
          sx={{
            fontWeight: 'bold',
            marginBottom: 8,
          }}
        >
          Would you like to speak to a nurse now or schedule a consult for later?
        </Typography>
      ) : (
        <>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 'bold',
              marginBottom: 8,
            }}
          >
            {"You're eligible for a complimentary initial assessment"}
          </Typography>
          <Typography
            sx={{
              marginBottom: 5,
            }}
          >
            Choose to speak to a nurse now about your medical history and our services, or schedule for later.
          </Typography>
        </>
      )}
      <Stack spacing={4}>
        <Typography>{"What you'll need:"}</Typography>
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <CreditCardIcon />
          <Typography
            sx={{
              marginLeft: 2,
            }}
          >
            Medicare card number (or IHI)
          </Typography>
        </Stack>
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <CreditCardIcon />
          <Typography
            sx={{
              marginLeft: 2,
            }}
          >
            Commonwealth concession card (if applicable)
          </Typography>
        </Stack>
      </Stack>
      <AddDiscountCode showNoCodeText={!ffEnableINCVideoCall} />
      <CtaSection sx={{ alignItems: { sm: 'stretch' } }}>
        {isImmediateNurseConsultationEnabled &&
        immediateConsultVariant !== ImmediateNurseConsultationVariant.CONTROL ? (
          <>
            <CtaButton
              type="button"
              color="primary"
              size="large"
              variant="contained"
              onClick={() => {
                handleAnalyticsOnClick('immediate_nurse_consultation');
                handleOnNextClick();
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {isMobile ? (
                  <Stack>
                    <Typography
                      sx={{
                        fontWeight: 'bold',
                      }}
                    >
                      {speakToANurseNowText}
                    </Typography>
                    <Typography>{averageWaitText}</Typography>
                  </Stack>
                ) : (
                  <Stack
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography>
                      <Box
                        component="span"
                        sx={{
                          fontWeight: 'bold',
                        }}
                      >
                        {speakToANurseNowText}
                      </Box>
                    </Typography>
                    <Typography>{averageWaitText}</Typography>
                  </Stack>
                )}
              </Box>
            </CtaButton>
            {showScheduleForLaterButton && (
              <>
                <Typography
                  sx={{
                    alignSelf: 'center',
                  }}
                >
                  or
                </Typography>
                <CtaButton
                  type="button"
                  color="primary"
                  size="large"
                  variant="outlined"
                  onClick={() => {
                    handleAnalyticsOnClick('scheduled_consultation');
                    handleShowCalendar();
                  }}
                  startIcon={<CalendarTodayIcon />}
                >
                  {isMobile ? 'Schedule a consult for later' : 'Schedule for later'}
                </CtaButton>
              </>
            )}
          </>
        ) : (
          <CtaButton
            type="button"
            color="primary"
            size="large"
            variant="contained"
            onClick={() => {
              handleAnalyticsOnClick('scheduled_consultation');
              handleShowCalendar();
            }}
            startIcon={<CalendarTodayIcon />}
          >
            <Typography>Schedule</Typography>
          </CtaButton>
        )}
      </CtaSection>
    </Box>
  );
};
